import { useState, useEffect, useCallback } from 'react';
import { driftAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Check, X, GitCompareArrows } from 'lucide-react';

export function HierarchyDriftPanel({ onApplied }) {
  const [drifts, setDrifts] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const fetchDrifts = useCallback(async () => {
    setLoading(true);
    try {
      const [pend, all] = await Promise.all([driftAPI.list(), driftAPI.list({ status: 'all' })]);
      setDrifts(pend.data.drifts || []);
      setResolved((all.data.drifts || []).filter((d) => d.status !== 'pending'));
    } catch {
      toast.error('Failed to load hierarchy drift');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrifts(); }, [fetchDrifts]);

  const runDetect = async () => {
    setBusy('detect');
    try {
      const res = await driftAPI.detect();
      toast.success(`${res.data.new_drifts} new change(s) found · ${res.data.pending} pending`);
      await fetchDrifts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setBusy('');
    }
  };

  const act = async (id, kind) => {
    setBusy(id);
    try {
      const res = kind === 'apply' ? await driftAPI.apply(id) : await driftAPI.dismiss(id);
      toast.success(res.data.message);
      await fetchDrifts();
      if (kind === 'apply') onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy('');
    }
  };

  const renderRows = (list, withActions) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-medium text-xs uppercase">Person</TableHead>
          <TableHead className="font-medium text-xs uppercase">Field</TableHead>
          <TableHead className="font-medium text-xs uppercase">Change (TradeIQ → Zoho CRM)</TableHead>
          <TableHead className="font-medium text-xs uppercase">Detected</TableHead>
          <TableHead className="font-medium text-xs uppercase text-right">{withActions ? 'Actions' : 'Outcome'}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list.length === 0 ? (
          <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground text-sm">
            {withActions ? 'No drift — TradeIQ matches Zoho CRM' : 'Nothing reviewed yet'}
          </TableCell></TableRow>
        ) : list.map((d) => (
          <TableRow key={d.id} data-testid={`drift-row-${d.id}`}>
            <TableCell className="font-medium text-sm">
              {d.name}
              <span className="block text-xs text-muted-foreground">{d.email}</span>
            </TableCell>
            <TableCell><Badge variant="outline" className="rounded-sm text-xs font-normal">{d.field_label}</Badge></TableCell>
            <TableCell className="text-sm">
              <span className="text-muted-foreground line-through">{d.old_value}</span>
              <span className="mx-2">→</span>
              <span className="font-medium">{d.new_value}</span>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">{new Date(d.detected_at).toLocaleString()}</TableCell>
            <TableCell className="text-right">
              {withActions ? (
                <span className="inline-flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 rounded-sm" disabled={busy === d.id} onClick={() => act(d.id, 'apply')} data-testid={`drift-apply-${d.id}`}>
                    {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}Apply
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 rounded-sm" disabled={busy === d.id} onClick={() => act(d.id, 'dismiss')} data-testid={`drift-dismiss-${d.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" />Dismiss
                  </Button>
                </span>
              ) : (
                <Badge variant="outline" className={`rounded-sm text-xs font-normal ${d.status === 'applied' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-stone-100 text-stone-700'}`}>
                  {d.status} {d.resolved_by ? `· ${d.resolved_by}` : ''}
                </Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className="space-y-4" data-testid="hierarchy-drift-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <GitCompareArrows className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Changes made in Zoho CRM since the last sync. Nothing is applied automatically.
          </p>
        </div>
        <Button size="sm" variant="outline" className="ml-auto rounded-sm" disabled={busy === 'detect'} onClick={runDetect} data-testid="drift-scan-btn">
          {busy === 'detect' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Scan Zoho CRM now
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending" data-testid="drift-tab-pending">Needs Review ({drifts.length})</TabsTrigger>
            <TabsTrigger value="resolved" data-testid="drift-tab-resolved">Reviewed ({resolved.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="pending">
            <Card className="rounded-sm"><CardContent className="p-0">{renderRows(drifts, true)}</CardContent></Card>
          </TabsContent>
          <TabsContent value="resolved">
            <Card className="rounded-sm"><CardContent className="p-0">{renderRows(resolved, false)}</CardContent></Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
